import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { loadAndAssert } from '../authz/access.js';
import { ConfirmationProgressStatus, setStructureConfirmationProgressByExam } from '../confirmation-progress.js';
import { Conversation } from '../conversation.js';
import { Exam } from '../exam.js';
import { updateMessageByExam } from '../extraction-progress.js';
import { findPointInconsistencies } from '../utils/points-model.js';
import { nudgeExamJobs } from './jobs/job-store.js';
import { dispatchJobs } from './jobs/dispatch.js';
import { EXTRACTION_JOB_TYPE } from './jobs/job.js';

/**
 * Aggregator tasks whose stored points contradict the sum of their answerable children (empty =
 * consistent). Structure confirmation is gated on this by `runStructureConfirmationAgent`, the sole
 * confirm path — the button just sends the confirmation message that triggers the agent.
 */
export async function getStructurePointInconsistencies(examId: string) {
  const examCollection = await getCollection(Exam);
  const exam = await examCollection.findOne({ _id: examId }, { projection: { tasks: 1 } } as any);
  return findPointInconsistencies((exam?.tasks as any[]) ?? []);
}

export async function reopenStructureCanvas(conversationId: string): Promise<void> {
  try {
    const conversationCollection = await getCollection(Conversation);
    await conversationCollection.updateOne({ _id: conversationId }, { $set: { pendingCanvasOpen: 'structureeditor' } });
  } catch (err) {
    logger.warn({ err, conversationId }, 'Failed to reopen structure canvas after inconsistent points');
  }
}

/**
 * Opens the gate everything that needs a confirmed structure waits on — the shared second half of
 * `confirmStructureMutation` and of a generated exam's own auto-confirm (see `exam-creation.job.ts`),
 * pulled out so "the structure gate opens" has one definition instead of two that can drift.
 */
export async function openStructureGate(examId: string): Promise<void> {
  await setStructureConfirmationProgressByExam(examId, {
    status: ConfirmationProgressStatus.DONE,
    confirmed: true,
    message: 'progress.structureConfirmed',
  });

  // Also update the task extraction progress message so the UI doesn't keep showing the "waiting for confirmation" text.
  try {
    await updateMessageByExam(examId, 'task', 'progress.structureConfirmed');
  } catch (err) {
    logger.warn({ err, examId }, 'Failed to update task extraction message after structure confirmation');
  }

  // The confirmation is the gate for everything that needs a confirmed structure. It is now a QUEUE write
  // rather than a detached function call: the two extractions become rows, and the runner claims them under
  // their own locks so they run concurrently instead of the serialised pair the deleted
  // `continueAfterStructureConfirmation` had to use. A crash between the confirmation and the runner picking
  // them up costs latency, not work.
  //
  // Answer extraction is NOT started here any more. It is gated on the per-submission `splitConfirmed` flag,
  // which only the split confirmation knows about, so `confirmSubmissionSplitMutation` enqueues those — see
  // `jobs/types/answer-extraction.job.ts`.
  //
  // Both unconditionally: each type's `ready()` drops itself when no such file was uploaded, so there is
  // nothing to check here. That is the point of deriving readiness from the exam.
  logger.info({ examId }, 'Structure confirmed — queueing the extraction jobs');
  await dispatchJobs(EXTRACTION_JOB_TYPE.masterSolution, examId);
  await dispatchJobs(EXTRACTION_JOB_TYPE.gradingScheme, examId);

  // WAKES WHAT WAS ALREADY WAITING, which the enqueues above cannot: rows deferred on
  // `answerExtraction:structure-unconfirmed` are `pending` with a `runAfter` up to `WAIT_BACKOFF_MS` in the
  // future, so without this the confirmation a teacher just made takes up to 30 seconds to have any visible
  // effect. Readiness is derived, so the state write IS the release — this only makes it prompt.
  //
  // Idempotent and cheap: it only touches rows already waiting, and it is the last thing here for a reason —
  // waking a job before the state it will re-read is committed would just defer it again.
  await nudgeExamJobs(examId);
}

export const confirmStructureMutation = registerMutation({
  operationName: 'confirmStructureMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'conversationId', type: () => String },
    { name: 'messageId', type: () => String },
    { name: 'confirmedContent', type: () => String },
  ],
  resolve: (examId: string, conversationId: string, messageId: string, confirmedContent: string) => async () => {
    // Both, separately: the jobs below are dispatched on `examId`, which nothing ties to `conversationId`.
    // Outside the try below, so a denial is reported rather than turned into `false`.
    await loadAndAssert(Conversation, conversationId, 'update', { _id: 1 });
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    let updated = false;
    try {
      const conversationCollection = await getCollection(Conversation);
      const result = await conversationCollection.updateOne(
        { _id: conversationId },
        {
          $set: {
            'messages.$[target].content': confirmedContent,
            'messages.$[target].confirmed': true,
            'messages.$[target].confirmedBy': 'teacher',
          },
        },
        {
          arrayFilters: [
            {
              'target._id': messageId,
            },
          ],
        },
      );

      updated = ((result as any)?.modifiedCount ?? 0) > 0;
      if (!updated) {
        return false;
      }
    } catch (err) {
      logger.error({ err, examId }, 'Failed to update ConfirmationMessage content');
      return false;
    }

    if (!updated) {
      return false;
    }

    await openStructureGate(examId);

    return true;
  },
});

export const updateStructureMessageContentMutation = registerMutation({
  operationName: 'updateStructureMessageContentMutation',
  type: () => Boolean,
  params: [
    { name: 'conversationId', type: () => String },
    { name: 'messageId', type: () => String },
    { name: 'content', type: () => String },
  ],
  resolve: (conversationId: string, messageId: string, content: string) => async () => {
    await loadAndAssert(Conversation, conversationId, 'update', { _id: 1 });
    try {
      const conversationCollection = await getCollection(Conversation);
      const result = await conversationCollection.updateOne(
        { _id: conversationId },
        {
          $set: {
            'messages.$[target].content': content,
          },
        },
        {
          arrayFilters: [
            {
              'target._id': messageId,
            },
          ],
        },
      );

      return ((result as any)?.modifiedCount ?? 0) > 0;
    } catch (err) {
      logger.error({ err, conversationId, messageId, content }, 'Failed to persist structure message content');
      return false;
    }
  },
});
